// Utility to detect user's country and set appropriate defaults

export interface CountryDefaults {
  currency: "USD" | "EUR" | "GBP"
  unitSystem: "imperial" | "metric"
  temperatureUnit: "celsius" | "fahrenheit"
}

// Map of countries to their default currency and unit system
const COUNTRY_DEFAULTS: Record<string, CountryDefaults> = {
  US: { currency: "USD", unitSystem: "imperial", temperatureUnit: "fahrenheit" },

  AT: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  BE: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  CY: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  EE: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  FI: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  FR: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  DE: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  GR: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  IE: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  IT: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  LV: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  LT: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  LU: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  MT: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  NL: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  PT: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  SK: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  SI: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },
  ES: { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" },

  GB: { currency: "GBP", unitSystem: "imperial", temperatureUnit: "celsius" },

  AU: { currency: "USD", unitSystem: "metric", temperatureUnit: "celsius" },
  CA: { currency: "USD", unitSystem: "metric", temperatureUnit: "celsius" },
  NZ: { currency: "USD", unitSystem: "metric", temperatureUnit: "celsius" },
  BR: { currency: "USD", unitSystem: "metric", temperatureUnit: "celsius" },
  MX: { currency: "USD", unitSystem: "metric", temperatureUnit: "celsius" },
  JP: { currency: "USD", unitSystem: "metric", temperatureUnit: "celsius" },
  CN: { currency: "USD", unitSystem: "metric", temperatureUnit: "celsius" },
  IN: { currency: "USD", unitSystem: "metric", temperatureUnit: "celsius" },
}

const DEFAULT_PREFERENCES: CountryDefaults = {
  currency: "USD",
  unitSystem: "metric",
  temperatureUnit: "celsius",
}

const CACHE_KEY = 'surf-geolocation-cache'
const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours

/**
 * Detects user's country from IP address and returns appropriate defaults
 * 
 * Uses server-side API route to avoid CORS issues and rate limiting
 * Caches results in localStorage to minimize API calls across page reloads
 */
export async function detectUserDefaultsFromIP(): Promise<CountryDefaults> {
  try {
    // Check localStorage cache first (persists across page reloads)
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem(CACHE_KEY)
        if (cached) {
          const cacheData = JSON.parse(cached)
          const age = Date.now() - cacheData.timestamp
          
          if (age < CACHE_DURATION && cacheData.countryCode) {
            if (COUNTRY_DEFAULTS[cacheData.countryCode]) {
              console.log("Using cached geolocation:", cacheData.countryCode)
              return COUNTRY_DEFAULTS[cacheData.countryCode]
            }
          } else {
            // Cache expired, remove it
            localStorage.removeItem(CACHE_KEY)
          }
        }
      } catch (cacheError) {
        // Invalid cache, remove it
        localStorage.removeItem(CACHE_KEY)
      }
    }

    console.log("Detecting country from IP address...")
    
    // Use our server-side API route instead of calling ipapi.co directly
    // This avoids CORS issues and handles rate limiting gracefully
    const response = await fetch('/api/geolocation', {
      credentials: 'include',
      // Add timeout to prevent hanging
      signal: AbortSignal.timeout(5000), // 5 second timeout
    })
    
    if (!response.ok) {
      // If rate limited or error, fall back to browser locale (silently)
      // Don't log errors - rate limiting is expected and handled gracefully
      return DEFAULT_PREFERENCES
    }
    
    const data = await response.json()
    
    if (!data.success || !data.country_code) {
      return DEFAULT_PREFERENCES
    }
    
    const countryCode = data.country_code
    
    // Cache the result in localStorage (persists across page reloads)
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          countryCode,
          timestamp: Date.now(),
        }))
      } catch (storageError) {
        // localStorage might be full or disabled, ignore
      }
    }
    
    if (countryCode && COUNTRY_DEFAULTS[countryCode]) {
      console.log(`IP detected country: ${countryCode}, using defaults:`, COUNTRY_DEFAULTS[countryCode])
      return COUNTRY_DEFAULTS[countryCode]
    }
    
    return DEFAULT_PREFERENCES
  } catch (error: any) {
    // Silently fall back to browser locale - rate limiting/errors are expected
    // Don't log errors to avoid console spam
    return DEFAULT_PREFERENCES
  }
}

/**
 * Detects user's country from browser locale and returns appropriate defaults
 */
export function detectUserDefaults(): CountryDefaults {
  try {
    // Try to get country from browser locale
    const locale = navigator.language || (navigator as any).userLanguage
    console.log("Browser locale:", locale)

    // Check all available locales
    const allLocales = navigator.languages || [locale]
    console.log("All browser locales:", allLocales)

    // Extract country code (e.g., "en-US" -> "US", "fr-FR" -> "FR")
    const countryMatch = locale.match(/[-_]([A-Z]{2})$/i)
    if (countryMatch) {
      const countryCode = countryMatch[1].toUpperCase()
      console.log("Extracted country code:", countryCode)
      const defaults = COUNTRY_DEFAULTS[countryCode]
      if (defaults) {
        console.log(`Detected country: ${countryCode}, using defaults:`, defaults)
        return defaults
      } else {
        console.log(`No defaults found for country: ${countryCode}`)
      }
    } else {
      console.log("No country code found in locale:", locale)
    }

    // Try to find country code in any of the available locales
    for (const loc of allLocales) {
      const match = loc.match(/[-_]([A-Z]{2})$/i)
      if (match) {
        const countryCode = match[1].toUpperCase()
        console.log("Found country code in locale:", loc, "->", countryCode)
        const defaults = COUNTRY_DEFAULTS[countryCode]
        if (defaults) {
          console.log(`Using country: ${countryCode}, defaults:`, defaults)
          return defaults
        }
      }
    }

    // If no country code in locale, try to infer from language
    const langCode = locale.split(/[-_]/)[0].toLowerCase()
    console.log("Language code:", langCode)
    
    // Check all locales for language codes
    for (const loc of allLocales) {
      const lang = loc.split(/[-_]/)[0].toLowerCase()
      console.log("Checking language:", lang, "from locale:", loc)
      
      if (lang === "nl") {
        console.log("Dutch language detected, using Netherlands defaults")
        return COUNTRY_DEFAULTS.NL
      }
    }
    
    if (langCode === "en") {
      // English speakers default to US settings
      console.log("English detected, using US defaults")
      return COUNTRY_DEFAULTS.US
    } else if (langCode === "de" || langCode === "fr" || langCode === "es" || langCode === "it" || langCode === "nl") {
      console.log("European language detected, using EUR defaults")
      return { currency: "EUR", unitSystem: "metric", temperatureUnit: "celsius" }
    }

    console.log("Could not detect country, using defaults")
    return DEFAULT_PREFERENCES
  } catch (error) {
    console.error("Error detecting user defaults:", error)
    return DEFAULT_PREFERENCES
  }
}

/**
 * Gets currency symbol for display
 */
export function getCurrencySymbol(currency: "USD" | "EUR" | "GBP"): string {
  const symbols = {
    USD: "$",
    EUR: "€",
    GBP: "£",
  }
  return symbols[currency]
}
